import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import LandingPage from './pages/LandingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import { Toaster } from 'react-hot-toast';
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import GuestRoute from './components/auth/GuestRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MyHomePage from './pages/MyHomePage';
import ProjectListPage from './pages/ProjectListPage';
import ProjectCreatePage from './pages/ProjectCreatePage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectSettingsPage from './pages/ProjectSettingsPage';
import SpecListPage from './pages/SpecListPage';
import SpecEditPage from './pages/SpecEditPage';
import TestCaseListPage from './pages/TestCaseListPage';
import IssueListPage from './pages/IssueListPage';
import IssueDetailFullPage from './pages/IssueDetailFullPage';
import ProjectDashboardPage from './pages/ProjectDashboardPage';
import RequirementListPage from './pages/RequirementListPage';
import TestRunExecutionPage from './pages/TestRunExecutionPage';
import TestResultsPage from './pages/TestResultsPage';
// AdminUserManagementPage는 '멤버 및 초대 관리'로 통합됨 (CHG-060)
import SchedulePage from './pages/SchedulePage';
import ProjectChatPage from './pages/ProjectChatPage';
import SprintPage from './pages/SprintPage';
import ChatLayout from './layouts/ChatLayout';
import FilterLayout from './layouts/FilterLayout';
import DocumentLayout from './layouts/DocumentLayout';
import ChatWelcomePage from './pages/ChatWelcomePage';
import ChatChannelPage from './pages/ChatChannelPage';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingCreatePage from './pages/OnboardingCreatePage';
import OrgInvitationAcceptPage from './pages/OrgInvitationAcceptPage';
const OrgMemberManagePage = lazy(() => import('./pages/OrgMemberManagePage'));
const OrgAuditLogsPage = lazy(() => import('./pages/OrgAuditLogsPage'));
const AdminBugReportsPage = lazy(() => import('./pages/AdminBugReportsPage'));
const AdminTenantsPage = lazy(() => import('./pages/AdminTenantsPage'));

// Lazy-loaded pages (CHG-038)
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const GlobalFilterPage = lazy(() => import('./pages/GlobalFilterPage'));
// Lazy-loaded pages (CHG-040)
const GoalsPage = lazy(() => import('./pages/GoalsPage'));
const IdeasPage = lazy(() => import('./pages/ideas/IdeasPage'));
// Lazy-loaded pages (CHG-041)
const WorkflowListPage = lazy(() => import('./pages/WorkflowListPage'));
const WorkflowEditorPage = lazy(() => import('./pages/WorkflowEditorPage'));
const SettingsLayout = lazy(() => import('./pages/SettingsLayout'));
const SettingsGeneralPage = lazy(() => import('./pages/SettingsGeneralPage'));
const SecuritySettingsPage = lazy(() => import('./pages/SecuritySettingsPage'));
const IssueTypesAdminPage = lazy(() => import('./pages/IssueTypesAdminPage'));
const IssueTypeSchemesPage = lazy(() => import('./pages/IssueTypeSchemesPage'));
const SchemeEditPage = lazy(() => import('./pages/SchemeEditPage'));
const CustomFieldsAdminPage = lazy(() => import('./pages/CustomFieldsAdminPage'));
const IssueTypeFieldConfigPage = lazy(() => import('./pages/IssueTypeFieldConfigPage'));
// CHG-047: GitHub Phase 2
const ReviewDashboardPage = lazy(() => import('./pages/ReviewDashboardPage'));
const TraceabilityPage = lazy(() => import('./pages/TraceabilityPage'));
const MetricsDashboardPage = lazy(() => import('./pages/MetricsDashboardPage'));
const ReleasesPage = lazy(() => import('./pages/ReleasesPage'));
// CHG-049: Figma Phase 1
const DesignItemsPage = lazy(() => import('./pages/DesignItemsPage'));
const DesignItemDetailPage = lazy(() => import('./pages/DesignItemDetailPage'));
// CHG-055: 프로젝트 메뉴 유연화
const ProjectConfigPage = lazy(() => import('./pages/ProjectConfigPage'));
// CHG-090: 문서함
const TeamSpaceListPage = lazy(() => import('./pages/TeamSpaceListPage'));
const DocumentWelcomePage = lazy(() => import('./pages/DocumentWelcomePage'));
const DocumentEditorPage = lazy(() => import('./pages/DocumentEditorPage'));
// CHG-130 REQ-DHR-026/027: 프로젝트 문서함 (인페이지 뷰)
const ProjectDocumentLayout = lazy(() => import('./pages/ProjectDocumentLayout'));
// CHG-131 REQ-CP-019: 내 프로필 페이지 (GNB 드롭다운 진입)
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
// CHG-132 Phase D: 보관 프로젝트 읽기 권한 요청
const SpaceAccessRequestPage = lazy(() => import('./pages/SpaceAccessRequestPage'));
// CHG-132 Phase E REQ-PA-024: 조직 통합 보관 접근 기록
const OrgAccessRequestHistoryPage = lazy(() => import('./pages/OrgAccessRequestHistoryPage'));

// "/" 진입점: 비로그인 → 랜딩, 로그인 → 앱 대시보드(/home)
function RootPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <LandingPage />;
  return <Navigate to="/home" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            fontSize: '14px',
            borderRadius: '10px',
            padding: '12px 16px',
          },
        }}
      />
      <Routes>
        {/* 루트: 비로그인 → 랜딩, 로그인 → /home 리다이렉트 */}
        <Route path="/" element={<RootPage />} />

        {/* 법적 문서 공개 라우트 */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Guest routes */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <RegisterPage />
            </GuestRoute>
          }
        />

        {/* Email verification (CHG-060) */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Onboarding (CHG-060: REQ-B-004, 인증 필수, AppLayout 밖) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/create"
          element={
            <ProtectedRoute>
              <OnboardingCreatePage />
            </ProtectedRoute>
          }
        />

        {/* Organization invitation (CHG-060: 비인증 접근 허용) */}
        <Route path="/org-invitations/:token/accept" element={<OrgInvitationAcceptPage />} />

        {/* Project invitation route (독립 페이지, 레이아웃 없음) */}
        {/* CHG-141 BUG-002/003: /join 라우트 제거 — 미가입자도 /register?invitation=...&type=PROJECT로 통합 */}
        <Route path="/invitations/:token/accept" element={<InvitationAcceptPage />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<MyHomePage />} />

          {/* Projects */}
          <Route path="/projects" element={<ProjectListPage />} />
          <Route path="/projects/new" element={<ProjectCreatePage />} />
          <Route path="/projects/:key" element={<ProjectDetailPage />}>
            {/* index 리다이렉트는 ProjectDetailPage에서 defaultMenu 기반으로 처리 (REQ-V31-019) */}
            <Route path="dashboard" element={<ProjectDashboardPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="timeline" element={<Navigate to="../schedule" replace />} />
            <Route path="board" element={<Navigate to="../issues?tab=board" replace />} />
            <Route path="requirements" element={<RequirementListPage />} />
            <Route path="specifications" element={<SpecListPage />} />
            <Route path="specs/:specId" element={<SpecEditPage />} />
            <Route path="tests" element={<TestCaseListPage />} />
            <Route path="specs/:specId/test-runs/:runId" element={<TestRunExecutionPage />} />
            <Route path="specs/:specId/test-results" element={<TestResultsPage />} />
            <Route path="test-results" element={<Navigate to="../tests?tab=results" replace />} />
            <Route path="sprint" element={<SprintPage />} />
            <Route path="issues" element={<IssueListPage />} />
            <Route path="issues/:issueId" element={<IssueDetailFullPage />} />
            <Route path="chat" element={<ProjectChatPage />} />
            <Route path="design-items" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><DesignItemsPage /></Suspense>} />
            <Route path="design-items/:designItemId" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><DesignItemDetailPage /></Suspense>} />
            <Route path="settings" element={<ProjectSettingsPage />} />
            <Route path="code-review" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><ReviewDashboardPage /></Suspense>} />
            <Route path="traceability" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><TraceabilityPage /></Suspense>} />
            <Route path="metrics" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><MetricsDashboardPage /></Suspense>} />
            <Route path="releases" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><ReleasesPage /></Suspense>} />
            {/* CHG-130 REQ-DHR-026/027: 프로젝트 상단탭 "문서함" — 인페이지 뷰 */}
            <Route path="documents" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><ProjectDocumentLayout /></Suspense>}>
              <Route index element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><DocumentWelcomePage /></Suspense>} />
              <Route path=":documentId" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><DocumentEditorPage /></Suspense>} />
            </Route>
          </Route>

          {/* Global pages */}
          {/* CHG-131 REQ-CP-019: 내 프로필 — GNB 드롭다운에서만 진입 */}
          <Route path="/profile" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><ProfilePage /></Suspense>} />
          <Route path="/goals" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><GoalsPage /></Suspense>} />
          <Route path="/ideas" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><IdeasPage /></Suspense>} />
          <Route path="/glossary" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><GlossaryPage /></Suspense>} />
          <Route path="/issues" element={<PlaceholderPage title="작업" />} />
          <Route path="/requirements" element={<PlaceholderPage title="요구사항" />} />
          <Route path="/specifications" element={<PlaceholderPage title="명세" />} />
          <Route path="/tests" element={<PlaceholderPage title="테스트" />} />
          <Route path="/filters" element={<FilterLayout />}>
            <Route index element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><GlobalFilterPage /></Suspense>} />
            <Route path=":filterId" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><GlobalFilterPage /></Suspense>} />
          </Route>

          {/* Documents (CHG-090) */}
          <Route path="/documents" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><TeamSpaceListPage /></Suspense>} />
          {/* CHG-132 Phase D: 읽기 권한 요청 상세 (요청자/승인자 공용) */}
          <Route
            path="/access-requests/:projectId/:requestId"
            element={
              <Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}>
                <SpaceAccessRequestPage />
              </Suspense>
            }
          />
          <Route path="/documents/:spaceId" element={<DocumentLayout />}>
            <Route index element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><DocumentWelcomePage /></Suspense>} />
            <Route path=":documentId" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><DocumentEditorPage /></Suspense>} />
          </Route>

          {/* Chat (CHG-075) */}
          <Route path="/chat" element={<ChatLayout />}>
            <Route index element={<ChatWelcomePage />} />
            <Route path=":channelId" element={<ChatChannelPage />} />
          </Route>
          <Route path="/settings" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><SettingsLayout /></Suspense>}>
            <Route path="general" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><SettingsGeneralPage /></Suspense>} />
            <Route path="project-config" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><ProjectConfigPage /></Suspense>} />
            <Route path="members" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><OrgMemberManagePage /></Suspense>} />
            {/* CHG-140 REQ-OML-015 */}
            <Route path="audit-logs" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><OrgAuditLogsPage /></Suspense>} />
            <Route path="security" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><SecuritySettingsPage /></Suspense>} />
            <Route path="workflows" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><WorkflowListPage /></Suspense>} />
            <Route path="workflows/new" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><WorkflowEditorPage /></Suspense>} />
            <Route path="workflows/:workflowId/edit" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><WorkflowEditorPage /></Suspense>} />
            <Route path="issue-types" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><IssueTypesAdminPage /></Suspense>} />
            <Route path="issue-type-schemes" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><IssueTypeSchemesPage /></Suspense>} />
            <Route path="issue-type-schemes/:schemeId/edit" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><SchemeEditPage /></Suspense>} />
            <Route path="custom-fields" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><CustomFieldsAdminPage /></Suspense>} />
            <Route path="issue-types/:issueTypeId/field-config" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><IssueTypeFieldConfigPage /></Suspense>} />
            <Route path="tenants" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><AdminTenantsPage /></Suspense>} />
            <Route path="bug-reports" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><AdminBugReportsPage /></Suspense>} />
            {/* CHG-132 Phase E REQ-PA-024 */}
            <Route path="archive-access" element={<Suspense fallback={<div className="flex items-center justify-center h-64"><p className="text-sm text-gray-500">로딩 중...</p></div>}><OrgAccessRequestHistoryPage /></Suspense>} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500 mt-1">준비 중인 페이지입니다.</p>
      </div>
    </div>
  );
}

export default App;
